import {
  api,
  formatError,
  getApiKey,
  getCachedWorkspace,
  listRegisteredWorkspaces,
  upsertWorkspace,
  activateWorkspace,
  clearActiveWorkspace,
  saveWorkspaceToConfig,
  fetchAuthorizedWorkspaces,
  reconcileActiveWorkspace,
} from '../api-client'

interface MeResponse {
  slug?: string
  workspace_id?: string
  name?: string
}

// `driftless workspace <list|current|switch|add>` — navigate between the
// workspaces you belong to from the CLI. Each registered workspace carries its
// own scoped key; switching mirrors the chosen entry into the active config so
// every other command targets it. No single "super key" exists.
export async function workspaceCommand(args: string[]): Promise<void> {
  const sub = args[0]
  const isJSON = args.includes('--json')
  switch (sub) {
    case 'list':
      return listCmd(isJSON)
    case 'current':
      return currentCmd(isJSON)
    case 'switch':
      return switchCmd(args[1], isJSON)
    case 'add':
      return addCmd(args)
    default:
      console.log('Usage: driftless workspace <list|current|switch|add>')
      console.log('  list                 Show your registered workspaces (● = active)')
      console.log('  current              Show the active workspace')
      console.log('  switch <slug>        Make a registered workspace active')
      console.log('  add --key <drift_…>  Register a workspace by its API key')
      return
  }
}

/**
 * Reconcile the cached active slug against the workspaces the CURRENT key can
 * actually reach (/me/workspaces), repairing a stale cache as a side effect. This
 * is what makes `list`/`current` mark active based on what the key can USE — not
 * a slug it would 403 on. Returns:
 *   active   — the usable active slug (undefined when none / ambiguous)
 *   stale    — the cached slug that turned out to be unauthorized (for messaging)
 *   offline  — true when we couldn't reach /me/workspaces (cache left untouched)
 */
async function resolveUsableActive(): Promise<{ active?: string; stale?: string; offline: boolean }> {
  const cached = getCachedWorkspace().slug
  const authorized = await fetchAuthorizedWorkspaces()
  const verdict = reconcileActiveWorkspace(cached, authorized)
  if (verdict.action === 'unknown') {
    // Offline — don't repair on a network blip; mark active from the cache.
    return { active: cached, offline: true }
  }
  if (verdict.action === 'ok') {
    return { active: verdict.slug, offline: false }
  }
  if (verdict.action === 'repaired') {
    // Stale cache, exactly one usable workspace → repair the flat fields so the
    // rest of the CLI targets the right one, then mark IT active.
    const w = authorized!.find((a) => a.slug === verdict.slug)
    clearActiveWorkspace()
    if (w) saveWorkspaceToConfig(w.slug, w.id ?? w.workspace_id)
    return { active: verdict.slug, stale: cached && cached !== verdict.slug ? cached : undefined, offline: false }
  }
  // ambiguous / cleared — the cached slug is unusable and we can't pick one.
  clearActiveWorkspace()
  return { active: undefined, stale: cached, offline: false }
}

async function listCmd(isJSON: boolean): Promise<void> {
  const entries = listRegisteredWorkspaces()
  const { active, stale, offline } = await resolveUsableActive()
  if (isJSON) {
    console.log(
      JSON.stringify(
        {
          active: active ?? null,
          ...(stale ? { stale_active: stale } : {}),
          ...(offline ? { offline: true } : {}),
          workspaces: entries.map((e) => ({ slug: e.slug, id: e.id, name: e.name, active: e.slug === active })),
        },
        null,
        2,
      ),
    )
    return
  }
  if (entries.length === 0) {
    console.log('No workspaces registered yet.')
    console.log('  Log in:           driftless login')
    console.log('  Or add by key:    driftless workspace add --key drift_…')
    return
  }
  console.log('Workspaces:')
  for (const e of entries) {
    const mark = e.slug === active ? '●' : '○'
    console.log(`  ${mark} ${e.name || e.slug}  (${e.slug})`)
  }
  if (stale) {
    console.log('')
    console.log(`⚠ '${stale}' was marked active but the current API key can't use it — repaired.`)
  }
  console.log('')
  console.log('Switch: driftless workspace switch <slug>')
}

async function currentCmd(isJSON: boolean): Promise<void> {
  const { active, stale, offline } = await resolveUsableActive()
  const entry = listRegisteredWorkspaces().find((e) => e.slug === active)
  if (isJSON) {
    console.log(
      JSON.stringify(
        { slug: active ?? null, name: entry?.name ?? null, ...(stale ? { stale_active: stale } : {}), ...(offline ? { offline: true } : {}) },
        null,
        2,
      ),
    )
    return
  }
  if (!active) {
    if (stale) {
      console.log(`No usable active workspace — '${stale}' is not authorized for the current API key.`)
      console.log('  Pick one:  driftless workspace switch <slug>')
    } else {
      console.log('No active workspace. Run: driftless login')
    }
    return
  }
  console.log(`Active workspace: ${entry?.name ? entry.name + ' ' : ''}(${active})`)
  if (stale) console.log(`  (repaired from stale '${stale}')`)
}

async function switchCmd(slug: string | undefined, isJSON: boolean): Promise<void> {
  if (!slug) {
    console.error('Usage: driftless workspace switch <slug>')
    process.exit(1)
  }
  const entry = activateWorkspace(slug)
  if (!entry) {
    console.error(`'${slug}' is not in your registry.`)
    const known = listRegisteredWorkspaces().map((e) => e.slug)
    if (known.length) console.error(`  Registered: ${known.join(', ')}`)
    console.error('  Add it:  driftless workspace add --key <its-api-key>')
    process.exit(1)
  }
  // Best-effort scope probe (scoped credentials): confirm the active key can
  // actually reach the target. Non-blocking — the local switch already happened;
  // we only warn on a definitive 403 and stay quiet when offline/timed-out, so
  // the "switch succeeds but every command 403s" confusion is surfaced up front.
  let warning: string | undefined
  try {
    await api.get(`/workspaces/${entry.slug}/topics?limit=1`, { apiKey: entry.api_key, timeoutMs: 5000, retries: 0 })
  } catch (e) {
    if (/HTTP 403/.test((e as Error).message)) {
      warning = `your key isn't scoped to '${entry.slug}'. Create a key that includes it (Dashboard → Settings → API keys → Authorized workspaces), or commands here return 403.`
    }
  }
  if (isJSON) {
    console.log(JSON.stringify({ switched: true, slug: entry.slug, ...(warning ? { warning } : {}) }))
    return
  }
  console.log(`Switched to ${entry.name || entry.slug} (${entry.slug}).`)
  if (warning) console.log(`\n⚠ ${warning}`)
}

async function addCmd(args: string[]): Promise<void> {
  const keyIdx = args.indexOf('--key')
  // Default (no --key): register the currently active key (e.g. capture a
  // workspace you logged into before this command existed).
  const key = keyIdx !== -1 ? args[keyIdx + 1] : getApiKey()
  if (!key) {
    console.error('Provide a key:  driftless workspace add --key drift_…')
    process.exit(1)
  }
  if (!key.startsWith('drift_')) {
    console.error('Error: API key must start with "drift_".')
    process.exit(1)
  }

  let me: MeResponse
  try {
    me = (await api.get('/me', { apiKey: key, timeoutMs: 8000, retries: 1 })) as MeResponse
  } catch (e) {
    console.error(`Could not validate that key: ${formatError(e as Error)}`)
    process.exit(1)
    return
  }
  if (!me?.slug) {
    console.error('That key is valid but resolved no workspace.')
    process.exit(1)
    return
  }

  upsertWorkspace({ slug: me.slug, id: me.workspace_id, name: me.name, api_key: key })
  // First workspace registered → make it active so commands have a target.
  if (!getCachedWorkspace().slug) activateWorkspace(me.slug)

  if (args.includes('--json')) {
    console.log(JSON.stringify({ added: true, slug: me.slug, name: me.name ?? null }))
    return
  }
  console.log(`Added workspace ${me.name || me.slug} (${me.slug}).`)
  console.log(`  Switch to it:  driftless workspace switch ${me.slug}`)
}
